import {
	ChannelDetailsFields,
	PrimaryInfoResponse,
	ContractDetailsResponse,
	OfficerReportsResponse,
	ChannelContactDetails,
	TChannelOfficerFields,
	TChannelContactDetails,
	MetaResponse,
} from 'containers/ChannelManagement/Channel/ChannelDetails/types';
import { decrypt } from 'utils/codeGenerator';
import _, { isEmpty, transform } from 'lodash';

import { FixMeLater } from 'types';
import moment from 'moment';

const channelOfficerDefault = {
	emailAddres: null,
	mobileNo: null,
	name: '',
	telNo: null,
};

const channelContactDetailsDefault = [
	{
		name: '',
		telNo: null,
		position: null,
		department: null,
		emailAddress: null,
		mobileNo: null,
	},
];

const channelAddressDefault = [
	{
		locationBase: 'LOCAL',
		countryId: 175,
		area: '',
		regionId: null,
		provinceId: null,
		cityId: null,
		barangayId: null,
		buildingName: '',
		street: '',
		zipCodeId: null,
		id: 0,
	},
];
export const transformResponseToChannelDetails = (
	primaryInfo: PrimaryInfoResponse,
	contractDetails: ContractDetailsResponse,
	reports: OfficerReportsResponse,
	meta?: MetaResponse | null
): ChannelDetailsFields => {
	const contactDetails = formatContact<TChannelContactDetails>(
		primaryInfo?.channelContactDetails || [],
		'contactDetails'
	);
	const officer = formatContact<TChannelOfficerFields>(
		primaryInfo?.channelOfficer ? [primaryInfo.channelOfficer] : [],
		'officer'
	);

	const transformAddress = (address) => ({
		area: address?.area || null,
		barangayId: address?.barangayId || 0,
		buildingName: address?.buildingName || null,
		cityId: address?.cityId || null,
		countryId: address?.countryId || null,
		locationBase: address?.locationBase || null,
		provinceId: address?.provinceId || null,
		regionId: address?.regionId || null,
		street: address?.street || null,
		zipCodeId: address?.zipCodeId || null,
		id: address?.id || 0,
	});

	const channelAddress = primaryInfo?.channelAddresses?.map(transformAddress);

	const transformedData: FixMeLater = {
		data: {
			...(primaryInfo && {
				updatedAt: meta?.lastUpdated || '',
				createdAt: primaryInfo?.createdAt || '',
				createdBy: primaryInfo?.channelPrimaryInfo.createdBy || '',
				updatedBy: meta?.updatedBy || '',
				username: primaryInfo?.channelPrimaryInfo.updatedBy || '',
				id: primaryInfo?.id || 0,
				channelPrimaryInfo: {
					businessGroup: primaryInfo?.channelPrimaryInfo.businessGroup || '',
					otherBusinessType:
						primaryInfo?.channelPrimaryInfo.otherBusinessType || null,
					channelAccountName: primaryInfo?.channelPrimaryInfo.name || '',
					channelType: primaryInfo?.channelPrimaryInfo.channelType || '',
					settlementSetup:
						primaryInfo?.channelPrimaryInfo.settlementSetup || '',
					channelTransactionType:
						primaryInfo?.channelPrimaryInfo.channelTransactionType || '',
					posType: primaryInfo?.channelPrimaryInfo.posType || undefined,
					channelContactDetails:
						contactDetails.length > 0
							? contactDetails
							: channelContactDetailsDefault,
					inCfs: String(primaryInfo?.channelPrimaryInfo.inCfs) || 0,
					tin: primaryInfo?.channelPrimaryInfo.tin || '',
					logo: {
						url: primaryInfo?.channelPrimaryInfo.logo,
						objectKey: null,
					},
					channelOfficer: officer[0] || channelOfficerDefault,
					partnershipType:
						primaryInfo?.channelPrimaryInfo.partnershipType || '',
					businessTypeId: primaryInfo?.channelPrimaryInfo.businessTypeId || 0,
					businessType: primaryInfo?.channelPrimaryInfo.businessTypeId || '',
					channelAccountCode: primaryInfo?.channelPrimaryInfo.tpaAccount || '',
					channelAddresses:
						primaryInfo?.channelAddresses.length > 0
							? channelAddress
							: channelAddressDefault,
				},
			}),
			channelReports: reports ? transformChannelResult(reports) : null,
			channelContractDetails: contractDetails
				? transformContractDetails(contractDetails)
				: null,
		},
	};

	return transformedData;
};
function transformChannelResult(originalData) {
	const fetchReport = (type) =>
		originalData?.channelReports?.find((r) => r?.transmissionType === type) ||
		{};

	if (
		fetchReport('SFTP')?.config === null &&
		fetchReport('SMTP')?.config === null
	) {
		return;
	}
	const fetchReportSMTP = fetchReport('SMTP');
	const fetchReportSFTP = fetchReport('SFTP');
	const decryptedConfig = (report) =>
		decrypt(report?.config, process.env.REACT_APP_APP_KEY, true, true);

	const smtpValue = decryptedConfig(fetchReportSMTP);
	const sftpValue = decryptedConfig(fetchReportSFTP);

	const csrDefaults = {
		filenameFormat: '',
		uploadFrequency: '',
		uploadSchedule: '',
		schedule: [],
		csrColumns: [],
	};

	const convertTime = (time: string) => {
		if (typeof time !== 'string') {
			console.error('Invalid input: Expected a string');
			return;
		}
		if (!/^(0?\d|1\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(time)) {
			console.error('Invalid time format: Expected format HH:mm:ss', time);
			return;
		}
		return time.slice(0, -3).padStart(5, '0');
	};

	const getCsrConfig = (type) => {
		const csr = originalData?.channelReports?.find(
			(detail) => detail?.transmissionType === type
		);
		if (csr) {
			const schedule = csr.channelReportEvents
				.filter((e) => e?.eventType === 'UPLOAD')
				.map((e, index) => {
					const generateValue = csr?.channelReportEvents.filter(
						(v) => v?.eventType === 'GENERATE'
					)[index];
					return {
						coveredSchedule: e.coveredSchedule || '',
						coveredTimeTo:
							e.coveredTimeTo && e.coveredTimeTo !== ''
								? convertTime(e.coveredTimeTo)
								: '',
						generationTime:
							generateValue?.eventTime && generateValue?.eventTime !== ''
								? convertTime(generateValue?.eventTime)
								: '',
						coveredTimeFrom:
							e.coveredTimeFrom && e.coveredTimeFrom !== ''
								? convertTime(e.coveredTimeFrom)
								: '',
						uploadTime:
							e.eventTime && e.eventTime !== '' ? convertTime(e.eventTime) : '',
						uploadId: e?.id || 0,
						generateId: generateValue?.id || 0,
						pairId: e?.pairId || 0,
					};
				});

			return {
				filenameFormat: csr?.filenameFormat || '',
				uploadFrequency: csr?.uploadFrequency || '',
				uploadSchedule: csr?.uploadSchedule || '',
				id: csr?.id || '',
				schedule: schedule,
				csrColumns:
					csr?.channelReportColumns?.map((col) => col.reportFieldId) || [],
			};
		}

		return csrDefaults;
	};

	const getTransformedSftp = (sftp) => {
		const {
			path = '',
			password = '',
			port = '',
			host = '',
			username = '',
		} = sftp || {};
		return {
			path,
			password,
			port,
			host,
			username,
			id: fetchReportSFTP?.id || 0,
			csrConfig: getCsrConfig('SFTP'),
			uploadConfig: getCsrConfig('SFTP_UC'),
		};
	};

	const getTransformedSmtp = (smtp) => {
		const { sender = '', receivers = [] } = smtp || {};

		return {
			sender,
			receivers,
			id: fetchReportSMTP?.id || 0,
			csrConfig: getCsrConfig('SMTP'),
			uploadConfig: getCsrConfig('SMTP_UC'),
		};
	};

	return {
		sftp: getTransformedSftp(sftpValue),
		smtp: getTransformedSmtp(smtpValue),
	};
}

function transformContractDetails(originalData) {
	const {
		bondEndDate = null,
		depositConsolidation = null,
		bondAmount = null,
		depositMode = null,
		otherBusinessLines = null,
		settlementAgreement = null,
		thresholdAmount = null,
		accreditationFee = null,
		vatRate = null,
		bondStartDate = null,
		annualFee = null,
		vatType = null,
		bondType = null,
	} = originalData?.channelContractDetails || {};

	const {
		executionDate = null,
		terminationDate = null,
		autoRenew = null,
		renewalFrequency = null,
		frequencyInterval = 0,
	} = originalData?.channelTermsDuration || {};

	const {
		signedNda = null,
		auditFinancialStmt = null,
		dataPrivacyPat = null,
		nonDisclosure = null,
		secArticleIncorp = null,
		birCertReg = null,
		dataPrivacyPia = null,
		genInfoSheets = null,
		incomeTaxReturn = null,
		govtIds = null,
		secRegCert = null,
		businessPermit = null,
		certAuthority = null,
		secondAuditFinStmt = null,
		cbciAmlQuestion = null,
		companyProfile = null,
		thirdPartySecChecklist = null,
		corpSecCert = null,
	} = originalData?.channelAccreditation || {};

	const defaultCollection = {
		acctType: null,
		bankId: 0,
		acctNo: null,
		acctName: null,
		bankBranch: null,
		id: 0,
	};

	const transformCollection = (collection) => ({
		acctType: collection?.acctType || null,
		bankId: collection?.bankId || null,
		acctNo: collection?.acctNo || null,
		acctName: collection?.acctName || null,
		bankBranch: collection?.bankBranch || null,
		id: collection?.id || 0,
	});

	const channelCheckCollection =
		originalData?.channelCheckCollection?.map(transformCollection);

	const channelCashCollection =
		originalData?.channelCashCollection?.map(transformCollection);
	return {
		contractDetails: {
			id: originalData?.channelContractDetails?.id || 0, // what id should be placed here? the api_reponse.data.id or api_response.data.channelContractDetails.id?
			bondEndDate: bondEndDate
				? moment(bondEndDate).format('MM/DD/YYYY')
				: null,
			depositConsolidation,
			bondAmount: bondAmount || null,
			depositMode,
			otherBusinessLines,
			settlementAgreement,
			thresholdAmount: thresholdAmount || null,
			accreditationFee,
			vatRate,
			bondStartDate: bondStartDate
				? moment(bondStartDate).format('MM/DD/YYYY')
				: null,
			channelCheckCollection:
				channelCheckCollection && channelCheckCollection.length > 0
					? channelCheckCollection
					: [defaultCollection],
			annualFee,
			vatType,
			channelTermsDuration: {
				executionDate: executionDate
					? moment(executionDate).format('MM/DD/YYYY')
					: null,
				terminationDate: terminationDate
					? moment(terminationDate).format('MM/DD/YYYY')
					: null,
				autoRenew: typeof autoRenew === 'number' ? String(autoRenew) : null,
				renewalFrequency: renewalFrequency || null,
				frequencyInterval: frequencyInterval ? String(frequencyInterval) : null,
				id: originalData?.channelTermsDuration?.id || '',
			},
			channelAccreditation: {
				signedNda,
				auditFinancialStmt,
				dataPrivacyPat,
				nonDisclosure,
				secArticleIncorp,
				birCertReg,
				dataPrivacyPia,
				genInfoSheets,
				incomeTaxReturn,
				govtIds,
				secRegCert,
				businessPermit,
				certAuthority,
				secondAuditFinStmt,
				cbciAmlQuestion,
				companyProfile,
				thirdPartySecChecklist,
				corpSecCert,
				id: originalData?.channelAccreditation?.id || 0,
			},
			bondType,
			channelCashCollection:
				channelCashCollection && channelCashCollection.length > 0
					? channelCashCollection
					: [defaultCollection],
		},
	};
}

const concatArrayWithComma = (object) => {
	const concatProp = (prop) => {
		if (Array.isArray(object[prop]) && object[prop].length > 0) {
			object[prop] = object[prop].join(',');
		} else {
			object[prop] = '';
		}
	};

	concatProp('emailAddress');
	concatProp('telNo');
	concatProp('mobileNo');

	return object;
};
export const transformPrimaryInfoOriginalSchema = (
	primaryInfo,
	originalForm,
	remarks,
	userInfo
): FixMeLater => {
	const {
		partnershipType,
		businessGroup,
		posType,
		settlementSetup,
		businessTypeId,
		otherBusinessType,
		tin,
		logo,
		channelTransactionType,
		channelType,
		channelAccountName,
		channelAccountCode,
		channelAddresses,
		channelContactDetails,
		channelOfficer,
	} = primaryInfo;

	const updatedInfo = JSON.parse(JSON.stringify(originalForm));

	updatedInfo.channelPrimaryInfo.partnershipType = partnershipType;
	updatedInfo.channelPrimaryInfo.businessGroup = businessGroup;
	updatedInfo.channelPrimaryInfo.posType = posType;
	updatedInfo.channelPrimaryInfo.settlementSetup = settlementSetup;
	updatedInfo.channelPrimaryInfo.businessTypeId = businessTypeId;
	updatedInfo.channelPrimaryInfo.otherBusinessType = otherBusinessType;
	updatedInfo.channelPrimaryInfo.tin = tin;
	updatedInfo.channelPrimaryInfo.remarks = remarks; // TODO: confirm to BE if this is the field for update remarks
	updatedInfo.channelPrimaryInfo.logo = logo?.objectKey;
	updatedInfo.channelPrimaryInfo.channelTransactionType =
		channelTransactionType;
	updatedInfo.channelPrimaryInfo.channelType = channelType;
	updatedInfo.channelPrimaryInfo.name = channelAccountName;
	updatedInfo.channelPrimaryInfo.tpaAccount = channelAccountCode;
	updatedInfo.channelAddresses = channelAddresses;
	updatedInfo.channelContactDetails = channelContactDetails.map((contact) =>
		concatArrayWithComma(contact)
	);
	updatedInfo.channelOfficer = concatArrayWithComma(channelOfficer);

	updatedInfo.channelPrimaryInfo.updatedBy = userInfo.username;
	updatedInfo.channelPrimaryInfo.updatedByEmail = userInfo.emailAddress;

	return updatedInfo;
};

export const transformContractDetailsOriginalSchema = (contractDetails) => {};

export const transformReportsOriginalSchema = (reports) => {};

function formatContact<T>(data: T[], field: string): T[] {
	return (
		data &&
		data.map((item: any) => {
			if (item && typeof item.emailAddress === 'string') {
				item = {
					...item,
					emailAddress: item.emailAddress
						.split(',')
						.map((email) => email.trim())
						.filter((v) => v !== ''),
				};
			}
			if (item && typeof item.telNo === 'string') {
				item = {
					...item,
					telNo: item.telNo
						.split(',')
						.map((tel) => tel.trim())
						.filter((v) => v !== ''),
				};
			}
			if (item && typeof item.mobileNo === 'string') {
				item = {
					...item,
					mobileNo: item.mobileNo
						.split(',')
						.map((mobile) => mobile.trim())
						.filter((v) => v !== ''),
				};
			}
			return item;
		})
	);
}

function formatContactToString(data) {}
